'use client';

import { useState } from 'react';
import Link from 'next/link';
import { signIn } from 'next-auth/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AppleIcon, FacebookIcon, GoogleIcon } from '~/components/svgIcons';
import { useSignUpMn } from '~/lib/api';
import { useAppRouter } from '~/hooks';
import { toast } from 'sonner';
import { CheckCircle, Loader, Lock, Mail, User } from 'lucide-react';
import { cn } from '~/lib/utils';
import { Button } from '~/components/ui';
import { AuthContainer } from '../AuthContainer';
import { motion } from 'motion/react';

// Zod schema for signup form validation
const signUpSchema = z
  .object({
    name: z
      .string()
      .min(2, 'Name must be at least 2 characters')
      .max(100, 'Name must be less than 100 characters'),
    email: z.string().email('Please enter a valid email address'),
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .max(100, 'Password must be less than 100 characters')
      .regex(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
        'Password must contain at least one uppercase letter, one lowercase letter, and one number',
      ),
    confirmPassword: z.string(),
    terms: z
      .boolean()
      .refine(
        (val) => val === true,
        'You must accept the terms and conditions',
      ),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

type SignUpFormData = z.infer<typeof signUpSchema>;

interface SignUpFormProps {
  callbackUrl: string;
}

export function SignUpForm({ callbackUrl }: SignUpFormProps) {
  const router = useAppRouter();
  const [success, setSuccess] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignUpFormData>({
    resolver: zodResolver(signUpSchema),
    mode: 'onBlur',
  });

  const signUpMn = useSignUpMn({
    onSuccess: () => {
      setSuccess(true);
      setTimeout(() => router.push('/auth/signin'), 5000);
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleGoogleSignUp = () => {
    signIn('google', { callbackUrl });
  };

  const onSubmit = (data: SignUpFormData) => {
    setSubmittedEmail(data.email);
    signUpMn.mutate({
      email: data.email,
      password: data.password,
      name: data.name,
    });
  };

  if (success) {
    return (
      <AuthContainer>
        <div className="text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, type: 'spring' }}
            className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full mb-4"
          >
            <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
          </motion.div>

          <h1 className="mb-3 text-foreground">Check Your Email</h1>
          <p className="text-foreground-secondary mb-6">
            We&apos;ve sent a verification link to{' '}
            <span className="text-primary">{submittedEmail}</span>. Please check
            your email and click the link to verify your account.
          </p>

          <p className="text-foreground-secondary mb-6">
            You&apos;ll be redirected to the sign-in page shortly...
          </p>

          <div className="space-y-3">
            <Button
              onClick={() => router.push('/auth/signin')}
              className="w-full rounded-lg transition-opacity shadow-lg"
            >
              Go to Sign In
            </Button>
          </div>
        </div>
      </AuthContainer>
    );
  }

  return (
    <AuthContainer
      title="Create Account"
      description="Start scraping in minutes"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div>
          <label htmlFor="name" className="block text-foreground mb-2">
            Full Name
          </label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-foreground-muted" />
            <input
              id="name"
              type="text"
              placeholder="John Doe"
              className={cn(
                'w-full pl-10 pr-4 py-3 border border-input bg-input-background text-foreground rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent',
                errors.name ? 'error' : '',
              )}
              {...register('name')}
            />
            {errors.name && (
              <span className="text-red-500">{errors.name.message}</span>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="email" className="block text-foreground mb-2">
            Email Address
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-foreground-muted" />
            <input
              id="email"
              type="email"
              placeholder="you@example.com"
              className={cn(
                'w-full pl-10 pr-4 py-3 border border-input bg-input-background text-foreground rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent',
                errors.email ? 'error' : '',
              )}
              {...register('email')}
            />
            {errors.email && (
              <span className="text-red-500">{errors.email.message}</span>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="password" className="block text-foreground mb-2">
            Password
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-foreground-muted" />
            <input
              id="password"
              type="password"
              placeholder="Create a password"
              className={cn(
                'w-full pl-10 pr-4 py-3 border border-input bg-input-background text-foreground rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent',
                errors.password ? 'error' : '',
              )}
              {...register('password')}
            />
            {errors.password && (
              <span className="text-red-500">{errors.password.message}</span>
            )}
          </div>
        </div>

        <div>
          <label
            htmlFor="confirmPassword"
            className="block text-foreground mb-2"
          >
            Confirm Password
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-foreground-muted" />
            <input
              id="confirmPassword"
              type="password"
              placeholder="Confirm your password"
              className={cn(
                'w-full pl-10 pr-4 py-3 border border-input bg-input-background text-foreground rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent',
                errors.confirmPassword ? 'error' : '',
              )}
              {...register('confirmPassword')}
            />
            {errors.confirmPassword && (
              <span className="text-red-500">
                {errors.confirmPassword.message}
              </span>
            )}
          </div>
        </div>

        <label className="flex items-start gap-2">
          <input
            type="checkbox"
            className="w-4 h-4 mt-1 text-primary border-input rounded focus:ring-primary"
            {...register('terms')}
          />
          <span className="text-foreground-secondary inline-flex items-center flex-wrap">
            I agree to the{' '}
            <Link href="/terms" className="text-primary hover:opacity-80 mx-1">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link
              href="/privacy"
              className="text-primary hover:opacity-80 mx-1"
            >
              Privacy Policy
            </Link>
          </span>
        </label>
        {errors.terms && (
          <span className="text-red-500">{errors.terms.message}</span>
        )}

        <Button
          type="submit"
          className="w-full py-3 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity shadow-lg flex items-center justify-center gap-2"
        >
          {signUpMn.isPending ? (
            <Loader className="w-5 h-5 animate-spin" />
          ) : (
            <User className="w-5 h-5" />
          )}
          {signUpMn.isPending ? 'Creating Account...' : 'Create Account'}
        </Button>
      </form>

      {/* Social Sign Up Options */}
      <div className="mt-6">
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-card-border"></div>
          </div>
          <div className="relative flex justify-center">
            <span className="bg-card px-4 text-foreground-secondary">
              Or continue with
            </span>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-3 gap-3">
          <button
            type="button"
            onClick={handleGoogleSignUp}
            disabled={signUpMn.isPending}
            className="cursor-pointer flex items-center justify-center px-4 py-3 border border-card-border rounded-lg bg-secondary hover:bg-background-secondary transition-colors"
          >
            {signUpMn.isPending ? (
              <Loader className="w-5 h-5 animate-spin" />
            ) : (
              <GoogleIcon />
            )}
          </button>

          <button
            type="button"
            disabled={true}
            className="disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center px-4 py-3 border border-card-border rounded-lg bg-secondary hover:bg-background-secondary transition-colors"
          >
            <AppleIcon />
          </button>

          <button
            type="button"
            disabled={true}
            className="disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center px-4 py-3 border border-card-border rounded-lg bg-secondary hover:bg-background-secondary transition-colors"
          >
            <FacebookIcon />
          </button>
        </div>
      </div>

      <div className="mt-6 text-center">
        <p className="text-foreground-secondary">
          Already have an account?{' '}
          <Link href="/auth/signin" className="text-primary hover:opacity-80">
            Sign in
          </Link>
        </p>
      </div>
    </AuthContainer>
  );
}
